import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import Table, { TableProps } from '../Table';
import { useTableConfig, TableConfigOptions } from '../../../hooks/useTableConfig';
import { DraggableHeader } from './DraggableHeader';
import { ColumnSelector } from './ColumnSelector';
import { ColumnDragProvider } from './ColumnDragContext';

export interface ConfigurableTableProps<T extends object> extends TableProps<T> {

  configOptions?: TableConfigOptions;

  renderColumnSelector?: (selector: React.ReactNode) => React.ReactNode;

  /**
   * When true, render the Columns control into `columnPortalHost` (createPortal).
   * Omit `columnPickerPortal` for default placement above the table.
   */
  columnPickerPortal?: boolean;
  /** Target element for the column picker when `columnPickerPortal` is true. */
  columnPortalHost?: HTMLElement | null;

  hideColumnPicker?: boolean;

  skeleton?: React.ReactNode;
  
  spacing?: number;
}

const ConfigurableTable = <T extends object>(props: ConfigurableTableProps<T>) => {
  const {
    columns,
    data,
    configOptions,
    renderColumnSelector,
    skeleton,
    spacing = 0,
    columnPickerPortal,
    columnPortalHost,
    hideColumnPicker,
    ...rest
  } = props;


  const {
    configuredColumns,
    visibleKeys,
    toggleVisibility,
    reorderColumns,
    allColumns,
  } = useTableConfig(columns, configOptions);


  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 120,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [activeId, setActiveId] = useState<string | null>(null);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };


  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      reorderColumns(active.id as string, over.id as string);
    }

    setActiveId(null);
  };

  const selector = hideColumnPicker ? null : (
    <ColumnSelector
      columns={allColumns}
      visibleKeys={visibleKeys}
      onToggle={toggleVisibility}
    />
  );

  const spacingClass = spacing === 0 ? "space-y-0" : `space-y-${spacing}`;

  const columnPortalReady =
    !hideColumnPicker && columnPickerPortal === true && columnPortalHost != null;

  const portaledColumnPicker = columnPortalReady
    ? createPortal(
        <div className="inline-flex items-center relative z-30">
          {renderColumnSelector ? renderColumnSelector(selector) : selector}
        </div>,
        columnPortalHost!,
      )
    : null;

  const inlineColumnPicker =
    !hideColumnPicker && !columnPortalReady &&
    (renderColumnSelector ? (
      renderColumnSelector(selector)
    ) : (
      <div className="flex justify-end items-center gap-2 pr-1 mb-2 relative z-30">
        {selector}
      </div>
    ));

  return (
    <div className={`${spacingClass} w-full min-w-0`}>
      {portaledColumnPicker}
      {inlineColumnPicker}


      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToHorizontalAxis]}
      >

        <SortableContext
          items={configuredColumns.map((c) => c.key)}
          strategy={horizontalListSortingStrategy}
        >

          <ColumnDragProvider value={{ activeId }}>
            <Table
              {...rest}
              columns={configuredColumns}
              data={data}
              skeleton={skeleton}
              tableFixed={true}
              renderHeaderCell={(column, _index, defaultContent) => (
                <DraggableHeader
                  id={column.key}
                  isReorderable={column.isReorderable !== false}
                  align={column.align}
                >
                  {defaultContent}
                </DraggableHeader>
              )}
            />
          </ColumnDragProvider>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default ConfigurableTable;